import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromMembers from '../reducers/members.reducer';
import * as fromRoot from '@app/reducers';
import { Group } from '@app/shared/models/group';
import { Team } from '@app/shared/models/team';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { MembersActions } from '../actions';
import { ModalAttachGroupComponent } from '../modals/modal-attach-group/modal-attach-group.component';
import { ModalDeleteMemberComponent } from '../modals/modal-delete-member/modal-delete-member.component';
import { ModalDetachGroupComponent } from '../modals/modal-detach-group/modal-detach-group.component';
import { Identity } from '@app/shared/models/identity';
import { User } from '@app/shared/models/user';
import { Role } from '@app/shared/models/role';
import { ModalAttachRoleComponent } from '../modals/modal-attach-role/modal-attach-role.component';
import { ModalDetachRoleComponent } from '../modals/modal-detach-role/modal-detach-role.component';
import { Constant } from 'src/constant';
import { Column } from '@app/shared/modules/smash-data-table/smash-data-table/smash-data-table.component';
import { GetMetricStatisticInput } from '@smash-sdk/transfer/ejs/01-2024';

@Component({
  selector: 'app-member-details',
  templateUrl: './member-details.component.html',
  styleUrls: ['./member-details.component.scss']
})
export class MemberDetailsComponent implements OnInit, OnDestroy {

  activeIdentity$: Observable<Identity>;

  memberDetails$: Observable<User>;
  memberDetailsLoading$: Observable<boolean>;
  memberDetailsLoadingError$: Observable<any>;

  memberDomains$: Observable<any[]>;
  memberDomainsLoaded$: Observable<boolean>;
  memberDomainsLoading$: Observable<boolean>;
  memberDomainsLoadingError$: Observable<any>;

  memberGroups$: Observable<any[]>;
  memberGroupsLoaded$: Observable<boolean>;
  memberGroupsLoading$: Observable<boolean>;
  memberGroupsLoadingError$: Observable<any>;

  memberRoles$: Observable<any[]>;
  memberRolesLoaded$: Observable<boolean>;
  memberRolesLoading$: Observable<boolean>;
  memberRolesLoadingError$: Observable<any>;

  memberAttributableRoles$: Observable<Role[]>;
  memberAttributableGroups$: Observable<any[]>;

  memberEditionPending$: Observable<any>;
  memberEditionError$: Observable<any>;

  memberTransferCountSeries$: Observable<any[]>;
  memberTransferCountSeriesLoaded$: Observable<boolean>;
  memberTransferCountSeriesLoading$: Observable<boolean>;

  memberSizeCountSeries$: Observable<any[]>;
  memberSizeCountSeriesLoaded$: Observable<boolean>;
  memberSizeCountSeriesLoading$: Observable<boolean>;

  memberFileCountSeries$: Observable<any[]>;
  memberFileCountSeriesLoaded$: Observable<boolean>;
  memberFileCountSeriesLoading$: Observable<boolean>;

  subscriptions: Subscription[] = [];
  memberDetailsForm: FormGroup;
  submittedMemberInformations = false;
  initialMemberDetails: any;
  selectedTeam: Team;
  selectedGroup: Group;
  teamId: string;

  transferCountsChartTitle = 'CHART_TRANSFERS_COUNT_CHART_TITLE';
  transferSizeUploadedChartTitle = 'TRANSFER_SIZE_UPLOADED_COUNT_CHART_TITLE';
  fileCountsChartTitle = 'FILE_COUNTS_CHART_TITLE';

  teamColumnsEnabled: Column[] = [
    { id: 'teamName', colName: 'PREMIUM_ADMIN_MEMBER_DETAILS_TABLE_MEMBER_TEAMS_LABEL_NAME', type: 'string', removable: false },
    { id: 'roleNameTranslated', colName: 'PREMIUM_ADMIN_MEMBER_DETAILS_TABLE_MEMBER_TEAMS_LABEL_ROLE', type: 'string', removable: false },
    // { id: 'groupName', colName: 'PREMIUM_ADMIN_MEMBER_DETAILS_TABLE_MEMBER_TEAMS_GROUP_NAME', type: 'string', removable: false },
  ];

  roleListColumnsEnabled: Column[] = [
    { id: 'translatedName', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_ROLE_NAME', type: 'string', removable: false },
  ];

  groupColumnsEnabled: Column[] = [
    { id: 'name', colName: 'PREMIUM_ADMIN_MEMBER_DETAILS_TABLE_MEMBER_TEAMS_LABEL_NAME', type: 'string', removable: false },
    {
      id: 'provisioned', colName: 'PREMIUM_ADMIN_GROUPS_TABLE_LABEL_PROVISIONED', type: 'boolean', removable: false,
      translations: {
        true: 'PREMIUM_ADMIN_GROUPS_TABLE_LABEL_GROUP_PROVISIONED',
        false: 'PREMIUM_ADMIN_GROUPS_TABLE_LABEL_GROUP_CUSTOM',
      },
      default: 'PREMIUM_ADMIN_GROUPS_TABLE_LABEL_GROUP_CUSTOM'
    },];

  PERIODS: { name: string, units: { name: string, value: GetMetricStatisticInput['unit'] }[] }[] = [
    { name: '7_DAYS', units: [{ name: 'DAY', value: 'day' }] },
    { name: '4_WEEKS', units: [{ name: 'DAY', value: 'day' }, { name: 'WEEK', value: 'week' }] },
    { name: '3_MONTHS', units: [{ name: 'DAY', value: 'day' }, { name: 'WEEK', value: 'week' }, { name: 'MONTH', value: 'month' }] },
    { name: '12_MONTHS', units: [{ name: 'MONTH', value: 'month' }, { name: 'WEEK', value: 'week' }] },
  ];
  teamsList = [];

  selectedPeriod = this.PERIODS[0];
  selectedUnit = this.PERIODS[0].units[0];

  constructor(
    private store$: Store<fromRoot.State & fromMembers.State>,
    private route: ActivatedRoute,
    private router: Router,
    private readonly fb: FormBuilder,
    private dialog: DialogService
  ) {
    this.activeIdentity$ = this.store$.select(fromRoot.getActiveIdentity);

    this.subscriptions.push(this.route.params
      .pipe(mergeMap(params => {
        this.teamId = params.id;
        return [
          MembersActions.loadMember({ teamId: params.id, userId: params.value }),
          MembersActions.loadMemberGroups({ teamId: params.id, userId: params.value }),
          MembersActions.loadMemberRoles({ teamId: params.id, userId: params.value }),
          MembersActions.loadMemberTransferCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
          MembersActions.loadMemberFileCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
          MembersActions.loadMemberSizeCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
        ]
      }
      ))
      .subscribe(action => {
        this.store$.dispatch(action);
      })
    );
    this.memberDetails$ = this.store$.select(fromMembers.getMemberDetails);
    this.memberDetailsLoading$ = this.store$.select(fromMembers.getMemberDetailsLoading);
    this.memberDetailsLoadingError$ = this.store$.select(fromMembers.getMemberDetailsLoadingError);

    this.memberGroups$ = this.store$.select(fromMembers.getMemberGroups);
    this.memberGroupsLoaded$ = this.store$.select(fromMembers.getMemberGroupsLoaded);
    this.memberGroupsLoading$ = this.store$.select(fromMembers.getMemberGroupsLoading);
    this.memberGroupsLoadingError$ = this.store$.select(fromMembers.getMemberGroupsLoadingError);

    this.memberRoles$ = this.store$.select(fromMembers.getMemberRoles);
    this.memberRolesLoaded$ = this.store$.select(fromMembers.getMemberRolesLoaded);
    this.memberRolesLoading$ = this.store$.select(fromMembers.getMemberRolesLoading);
    this.memberRolesLoadingError$ = this.store$.select(fromMembers.getMemberRolesLoadingError);

    this.memberAttributableRoles$ = this.store$.select(fromMembers.getAttributableRoles);
    this.memberAttributableGroups$ = this.store$.select(fromMembers.getAttributableGroups);

    this.memberEditionPending$ = this.store$.select(fromMembers.getMemberEditionPending);
    this.memberEditionError$ = this.store$.select(fromMembers.getMemberEditionError);

    this.memberTransferCountSeries$ = this.store$.pipe(select(fromMembers.getMemberTransferCountSeries));
    this.memberTransferCountSeriesLoaded$ = this.store$.pipe(select(fromMembers.getMemberTransferCountSeriesLoaded));
    this.memberTransferCountSeriesLoading$ = this.store$.pipe(select(fromMembers.getMemberTransferCountSeriesLoading));

    this.memberSizeCountSeries$ = this.store$.pipe(select(fromMembers.getMemberSizeCountSeries));
    this.memberSizeCountSeriesLoaded$ = this.store$.pipe(select(fromMembers.getMemberSizeCountSeriesLoaded));
    this.memberSizeCountSeriesLoading$ = this.store$.pipe(select(fromMembers.getMemberSizeCountSeriesLoading));

    this.memberFileCountSeries$ = this.store$.pipe(select(fromMembers.getMemberFileCountSeries));
    this.memberFileCountSeriesLoaded$ = this.store$.pipe(select(fromMembers.getMemberFileCountSeriesLoaded));
    this.memberFileCountSeriesLoading$ = this.store$.pipe(select(fromMembers.getMemberFileCountSeriesLoading));
  }

  ngOnInit(): void {
    this.initMemberDetailsForm();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }

  initMemberDetailsForm() {
    this.memberDetailsForm = this.fb.group({
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      username: ['', [Validators.required, Validators.pattern(Constant.regexpEmail)]],
    });

    this.subscriptions.push(
      this.memberEditionError$.pipe().subscribe(error => {
        if (error) {
          this.memberDetailsForm.controls[error.details.name].setErrors({ 'incorrect': true });
        }
      })
    );
  }

  get f() { return this.memberDetailsForm.controls; }

  updateMemberInformations() {
    this.submittedMemberInformations = true;
    if (this.memberDetailsForm.valid) {
      this.route.params.pipe(map(params =>
        MembersActions.updateMember({ teamId: params.id, data: this.memberDetailsForm.value })
      )).subscribe(action => {
        this.store$.dispatch(action);
      });
    }
  }

  async openModalDeleteMember() {
    const memberDetails = await this.memberDetails$.pipe(first(details => !!details)).toPromise();
    this.dialog.open(ModalDeleteMemberComponent, {
      enableClose: true,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberToDelete: memberDetails,
      }
    });
    event.stopPropagation();
    return false;
  }

  async openModalAttachRoleToMember() {
    const memberDetails = await this.memberDetails$.pipe(first(details => !!details)).toPromise();
    const attributableRoles = await this.memberAttributableRoles$.pipe(first(roles => !!roles)).toPromise();
    this.dialog.open(ModalAttachRoleComponent, {
      enableClose: false,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberDetails,
        attributableRoles,
      }
    });
  }

  async openModalDetachRoleFromMember(role: Role) {
    const memberDetails = await this.memberDetails$.pipe(first(details => !!details)).toPromise();
    this.dialog.open(ModalDetachRoleComponent, {
      enableClose: false,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberDetails,
        roleToDetach: role,
      }
    });
  }

  async openModalAttachNewGroup() {
    const memberDetails = await this.memberDetails$.pipe(first(details => !!details)).toPromise();
    const attributableGroups = await this.memberAttributableGroups$.pipe(first(groups => !!groups)).toPromise();
    this.dialog.open(ModalAttachGroupComponent, {
      enableClose: false,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberToAttach: memberDetails,
        attributableGroups,
      }
    });
  }

  async openModalDetachUserFromGroup(group: Group) {
    const memberDetails = await this.memberDetails$.pipe(first(details => !!details)).toPromise();
    this.dialog.open(ModalDetachGroupComponent, {
      enableClose: true,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberToDetach: memberDetails,
        group,
      }
    });
  }

  back() {
    this.subscriptions.push(this.route.params.subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/members');
    }));
  }

  goToGroupDetails(groupId: string) {
    this.subscriptions.push(this.route.params.subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/groups/' + groupId);
    }));
  }

  isDomainDetachable(item: any) {
    return !item?.groupName;
  }

  isGroupDetachable(item: any) {
    return !item.provisioned;
  }

  loadCharts(): void {
    this.route.params
      .pipe(mergeMap(params => [
        MembersActions.loadMemberTransferCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
        MembersActions.loadMemberSizeCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
        MembersActions.loadMemberFileCount({ teamId: params.id, user: params.value, period: this.selectedPeriod.name, unit: this.selectedUnit.value }),
      ]))
      .subscribe(action => {
        this.store$.dispatch(action);
      })
      .unsubscribe();
  }

  selectPeriod(period): void {
    this.selectedPeriod = period;
    this.selectedUnit = period.units[0];
    this.loadCharts();
  }

  selectUnit(unit): void {
    this.selectedUnit = unit;
    this.loadCharts();
  }

  refreshRoles(): void {
    this.route.params.pipe(map(params =>
      MembersActions.loadMemberRoles({ teamId: params.id, userId: params.value })
    )).subscribe(action => {
      this.store$.dispatch(action);
    });
  }
}
